import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { deformWave, roundFlight, lensShader } from "./wave-shaders.js";
import defaults from "./wave-defaults.json";

// Inspired by Franky Hung's wave propagation tutorial (Codrops, July 2026).
const canvas = document.querySelector(".wave-grid");
export let createGridFlight = () => null;
if (canvas) startGrid(canvas);

function startGrid(canvas) {
  const settings = { ...defaults };
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  } catch {
    canvas.hidden = true;
    return;
  }

  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#f4f3f1");
  const camera = new THREE.PerspectiveCamera(31, 1, 0.1, 80);
  camera.up.set(0, 0, -1);
  camera.position.set(0, 20, 0.8);
  camera.lookAt(0, 0, 0);
  camera.updateMatrixWorld();
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;

  const ambient = new THREE.AmbientLight(0xffffff, settings.ambient);
  scene.add(ambient);
  const light = new THREE.DirectionalLight(0xffffff, 1.5);
  light.position.set(-6, 12, 4);
  light.castShadow = true;
  light.shadow.mapSize.set(1024, 1024);
  light.shadow.radius = 4;
  light.shadow.bias = -0.0002;
  light.shadow.normalBias = 0.025;
  Object.assign(light.shadow.camera, {
    left: -18,
    right: 18,
    top: 18,
    bottom: -18,
    near: 0.1,
    far: 45,
  });
  scene.add(light);

  let darkMode = document.cookie.split(';').some(cookie => cookie.trim() === 'darkMode=true');
  let darkness = Number(darkMode);
  const spotlight = new THREE.SpotLight(0xffeee0, 0, 40, Math.PI / 4, 0.85, 2);
  spotlight.position.set(0, 14, -2);
  spotlight.target.position.set(0, 0, -1);
  spotlight.castShadow = true;
  spotlight.shadow.mapSize.set(1024, 1024);
  spotlight.shadow.bias = -0.0002;
  spotlight.shadow.normalBias = 0.025;
  spotlight.visible = false;
  scene.add(spotlight, spotlight.target);

  const size = 32;
  const presses = new Map();
  function createGeometry() {
    const width = settings.shapeSize;
    switch (settings.shape) {
      case "rounded":
        return new RoundedBoxGeometry(width, 2, width, 1, Math.min(0.12, width / 4));
      case "sphere":
        return new THREE.SphereGeometry(width / 2, 32, 16).translate(0, 1 - width / 2, 0);
      case "cylinder":
        return new THREE.CylinderGeometry(width / 2, width / 2, 2, 32);
      case "hexagon":
        return new THREE.CylinderGeometry(width / 2, width / 2, 2, 6);
      case "bauhaus": {
        const radius = (width * 0.96) / 2;
        const halfCircle = new THREE.Shape();
        halfCircle.absarc(0, 0, radius, 0, Math.PI, false);
        halfCircle.closePath();
        return new THREE.ExtrudeGeometry(halfCircle, { depth: 2, bevelEnabled: false, curveSegments: 16 })
          .rotateX(-Math.PI / 2)
          .translate(0, -1, radius / 2);
      }
      default:
        return new THREE.BoxGeometry(width, 2, width);
    }
  }
  let currentShape = settings.shape;
  let currentSize = settings.shapeSize;
  const material = new THREE.MeshPhongMaterial({ color: 0xffffff, shininess: 12, alphaToCoverage: true });
  let cubes = new THREE.InstancedMesh(createGeometry(), material, size ** 2);
  cubes.castShadow = true;
  cubes.receiveShadow = true;
  cubes.frustumCulled = false;
  const grid = new THREE.Group();
  grid.add(cubes);
  scene.add(grid);

  let aboutFlight;
  const matrix = new THREE.Matrix4();
  const base = new THREE.Color("#f4f3f1");
  const highlight = new THREE.Color("#ff0058").lerp(base, 0.7);
  const trailData = new Float32Array(128 * 4);
  const trailTexture = new THREE.DataTexture(trailData, 128, 1, THREE.RGBAFormat, THREE.FloatType);
  trailTexture.needsUpdate = true;
  const waveUniforms = {
    waveTrail: { value: trailTexture },
    waveCount: { value: 0 },
    waveTime: { value: 0 },
    waveBase: { value: base },
    waveHighlight: { value: highlight },
    panelColor: { value: new THREE.Color("#ff0058") },
    panelWhite: { value: new THREE.Color("white") },
    colorTarget: { value: 0 },
    colorElapsed: { value: 1000000 },
    colorDuration: { value: 0.25 },
    surfaceStrength: { value: settings.opacity },
    waveRigid: { value: settings.shape === "box" ? 0 : 1 },
    cursorPoint: { value: new THREE.Vector2() },
    cursorPresence: { value: 0 },
  };
  const waveKeys = [
    "waveAmplitude",
    "waveSpeed",
    "waveWidth",
    "waveFrequency",
    "waveFade",
    "waveAttack",
    "cursorRadius",
    "cursorDepth",
  ];
  for (const key of waveKeys) waveUniforms[key] = { value: settings[key] };
  function shadeSurface(shader, moving) {
    Object.assign(shader.uniforms, waveUniforms);
    shader.vertexShader = (
      moving
        ? deformWave(shader.vertexShader)
        : shader.vertexShader
            .replace("#include <common>", "#include <common>\nvarying float waveHeight;")
            .replace("#include <begin_vertex>", "#include <begin_vertex>\nwaveHeight = 0.0;")
    ).replace("#include <common>", `
      #include <common>
      attribute float colorFrom;
      attribute float colorDelay;
      uniform float colorTarget;
      uniform float colorElapsed;
      uniform float colorDuration;
      varying float panelMix;
    `).replace("#include <begin_vertex>", `
      #include <begin_vertex>
      panelMix = mix(colorFrom, colorTarget, smoothstep(0.0, colorDuration, colorElapsed - colorDelay));
    `);
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `
        #include <common>
        varying float waveHeight;
        uniform vec3 waveBase;
        uniform vec3 waveHighlight;
        varying float panelMix;
        uniform vec3 panelColor;
        uniform vec3 panelWhite;
        uniform float surfaceStrength;
      `,
      )
      .replace(
        "#include <color_fragment>",
        `
        #include <color_fragment>
        vec3 surfaceColor = mix(panelColor, panelWhite, panelMix);
        diffuseColor.rgb *= surfaceColor;
        diffuseColor.rgb *= mix(waveBase, waveHighlight, clamp(waveHeight * 1.4, 0.0, 0.7));
      `,
      )
      .replace(
        "#include <opaque_fragment>",
        `
        outgoingLight = mix(surfaceColor, outgoingLight, surfaceStrength);
        #include <opaque_fragment>
      `,
      );
  }
  material.onBeforeCompile = (shader) => {
    shadeSurface(shader, true);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float flightAmount;\nuniform float colorTarget;')
      .replace('vec3 surfaceColor = mix(panelColor, panelWhite, panelMix);', `
        float flightColor = mix(panelMix, 1.0 - colorTarget, smoothstep(0.0, 0.8, flightAmount));
        vec3 surfaceColor = mix(panelColor, panelWhite, flightColor);
      `)
      .replace('outgoingLight = mix(surfaceColor, outgoingLight, surfaceStrength);', `
        // HTML is fully opaque at 0.92; keep the cube for another ~55ms.
        if (flightAmount >= 0.999) discard;
        outgoingLight = mix(surfaceColor, outgoingLight, surfaceStrength);
        outgoingLight = mix(outgoingLight, surfaceColor, smoothstep(0.55, 0.8, flightAmount));
      `);
    shader.fragmentShader = roundFlight(shader.fragmentShader, true);
  };
  const backingMaterial = new THREE.MeshPhongMaterial({ color: 0xffffff, shininess: 12 });
  backingMaterial.onBeforeCompile = (shader) => shadeSurface(shader, false);
  const backing = new THREE.Mesh(new THREE.PlaneGeometry(size * 2, size * 2, size * 2, size * 2).rotateX(-Math.PI / 2), backingMaterial);
  backing.receiveShadow = true;
  function initColorAttributes(geometry, count, instanced) {
    const Attribute = instanced ? THREE.InstancedBufferAttribute : THREE.BufferAttribute;
    geometry.setAttribute("colorFrom", new Attribute(new Float32Array(count).fill(waveUniforms.colorTarget.value), 1));
    geometry.setAttribute("colorDelay", new Attribute(new Float32Array(count), 1));
  }
  initColorAttributes(backing.geometry, backing.geometry.getAttribute("position").count, false);
  grid.add(backing);
  cubes.customDepthMaterial = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, alphaHash: true });
  cubes.customDepthMaterial.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, waveUniforms);
    shader.vertexShader = deformWave(shader.vertexShader);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float flightAmount;')
      .replace('#include <alphahash_fragment>', `
        diffuseColor.a *= 1.0 - smoothstep(0.72, 0.92, flightAmount);
        #include <alphahash_fragment>
      `);
    shader.fragmentShader = roundFlight(shader.fragmentShader);
  };
  function updateLayout() {
    const hex = settings.shape === "hexagon";
    const round = settings.shape === "cylinder" || settings.shape === "sphere";
    const staggered = hex || round;
    const bauhaus = settings.shape === "bauhaus";
    const radius = settings.shapeSize / 2;
    // CylinderGeometry starts with a vertex on +Z: pointy-top hexes.
    const stepX = hex ? Math.sqrt(3) * radius : round ? 2 * radius : 1;
    const stepZ = hex ? 1.5 * radius : round ? Math.sqrt(3) * radius : 1;
    // Keep the same background coverage for tightly packed hexes and circles.
    const columns = staggered ? Math.ceil(size / stepX) : size;
    const rows = staggered ? Math.ceil(size / stepZ / 2) * 2 : size;
    const count = columns * rows * (bauhaus ? 2 : 1);
    if (count !== cubes.count) {
      const previous = cubes;
      cubes = new THREE.InstancedMesh(previous.geometry, material, count);
      cubes.customDepthMaterial = previous.customDepthMaterial;
      cubes.castShadow = true;
      cubes.receiveShadow = true;
      cubes.frustumCulled = false;
      grid.remove(previous);
      previous.dispose();
      grid.add(cubes);
    }
    for (let z = 0, index = 0; z < rows; z += 1) {
      const rowOffset = staggered ? (((z % 2) - 0.5) * stepX) / 2 : 0;
      for (let x = 0; x < columns; x += 1) {
        const centerX = (x - (columns - 1) / 2) * stepX + rowOffset;
        const centerZ = (z - (rows - 1) / 2) * stepZ;
        if (bauhaus) {
          // Two independently clickable halves per tile: split circles or opposing arcs.
          const variant = (x * 3 + z * 5 + Math.floor(x / 3)) % 4;
          const angle = variant >= 2 ? Math.PI / 2 : 0;
          for (let half = 0; half < 2; half += 1) {
            const offset = half === 0 ? -0.25 : 0.25;
            matrix.makeRotationY(angle + (half ^ (variant % 2)) * Math.PI);
            matrix.setPosition(centerX + Math.sin(angle) * offset, -1, centerZ + Math.cos(angle) * offset);
            cubes.setMatrixAt(index++, matrix);
          }
        } else {
          matrix.makeTranslation(centerX, -1, centerZ);
          cubes.setMatrixAt(index++, matrix);
        }
      }
    }
    cubes.instanceMatrix.needsUpdate = true;
    cubes.geometry.setAttribute(
      "clickPress",
      new THREE.InstancedBufferAttribute(new Float32Array(count), 1).setUsage(THREE.DynamicDrawUsage),
    );
    initColorAttributes(cubes.geometry, count, true);
    for (const name of ['flightX', 'flightY', 'flightZ', 'flightOrigin']) {
      cubes.geometry.setAttribute(name, new THREE.InstancedBufferAttribute(new Float32Array(count * 3), 3).setUsage(THREE.DynamicDrawUsage));
    }
    cubes.geometry.setAttribute('flightProgress', new THREE.InstancedBufferAttribute(new Float32Array(count), 1).setUsage(THREE.DynamicDrawUsage));
    cubes.geometry.setAttribute('flightShape', new THREE.InstancedBufferAttribute(new Float32Array(count * 4), 4).setUsage(THREE.DynamicDrawUsage));
    presses.clear();
  }
  updateLayout();

  const renderTarget = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples: 4 });
  const composer = new EffectComposer(renderer, renderTarget);
  composer.addPass(new RenderPass(scene, camera));
  const lens = new ShaderPass(lensShader);
  lens.uniforms.resolution.value = new THREE.Vector2();
  lens.uniforms.bentoRects.value = Array.from({length: 10}, () => new THREE.Vector4());
  composer.addPass(lens);
  composer.addPass(new OutputPass());
  const trail = [];
  const raycaster = new THREE.Raycaster();
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0));
  const worldPlane = new THREE.Plane();
  const pointer = new THREE.Vector2();
  const tilt = new THREE.Vector2();
  const tiltTarget = new THREE.Vector2();
  const hit = new THREE.Vector3();
  const lastPoint = new THREE.Vector2();
  const smoothPoint = new THREE.Vector2();
  const targetPoint = new THREE.Vector2();
  let hasPointer = false;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let time = 0;
  let colorStart = -1000000;
  let previousTime;
  let lastSample = -1;
  let pressGesture;

  // Pick one rendered pixel with the same deformation as the visible mesh and its shadow.
  const pickTarget = new THREE.WebGLRenderTarget(1, 1);
  const pickCamera = camera.clone();
  const pickPixel = new Uint8Array(4);
  const pickBackground = new THREE.Color(0);
  const pickMaterial = new THREE.ShaderMaterial({
    uniforms: waveUniforms,
    toneMapped: false,
    blending: THREE.NoBlending,
    vertexShader: deformWave(`
      #include <common>
      varying float objectId;
      void main() {
        #include <begin_vertex>
        objectId = float(gl_InstanceID + 1);
        gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(transformed, 1.0);
      }
    `),
    fragmentShader: roundFlight(`
      varying float objectId;
      varying float flightAmount;
      void main() {
        if (flightAmount >= 0.999) discard;
        gl_FragColor = vec4(mod(objectId, 256.0), mod(floor(objectId / 256.0), 256.0),
          floor(objectId / 65536.0), 255.0) / 255.0;
      }
    `),
  });

  function pickElement(event) {
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return -1;
    pickCamera.copy(camera);
    pickCamera.setViewOffset(rect.width, rect.height, Math.floor(x), Math.floor(y), 1, 1);
    const background = scene.background;
    const target = renderer.getRenderTarget();
    const shadows = renderer.shadowMap.enabled;
    try {
      scene.background = pickBackground;
      scene.overrideMaterial = pickMaterial;
      backing.visible = false;
      renderer.shadowMap.enabled = false;
      renderer.setRenderTarget(pickTarget);
      renderer.render(scene, pickCamera);
      renderer.readRenderTargetPixels(pickTarget, 0, 0, 1, 1, pickPixel);
    } finally {
      scene.background = background;
      scene.overrideMaterial = null;
      backing.visible = true;
      renderer.shadowMap.enabled = shadows;
      renderer.setRenderTarget(target);
    }
    return pickPixel[0] + pickPixel[1] * 256 + pickPixel[2] * 65536 - 1;
  }

  function applySettings() {
    if (settings.shape !== currentShape || settings.shapeSize !== currentSize) {
      const previousGeometry = cubes.geometry;
      cubes.geometry = createGeometry();
      previousGeometry.dispose();
      currentShape = settings.shape;
      currentSize = settings.shapeSize;
      waveUniforms.waveRigid.value = settings.shape === "box" ? 0 : 1;
      updateLayout();
    }
    for (const key of waveKeys) waveUniforms[key].value = settings[key];
    // Stay below the lowest possible element, including waves and click depression.
    backing.position.y = -2 - settings.waveAmplitude - settings.cursorDepth - settings.clickDepth - 0.1;
    for (const key of ["aberration", "blurBase", "blurEdge", "vignette", "edgeStart", "edgeSoftness"]) {
      lens.uniforms[key].value = settings[key];
    }
    base.set(settings.baseColor);
    highlight.copy(base).lerp(new THREE.Color(settings.brandColor), settings.colorMix);
    scene.background.copy(waveUniforms.panelColor.value).lerp(new THREE.Color("white"), waveUniforms.colorTarget.value);
    waveUniforms.surfaceStrength.value = settings.opacity * (reducedMotion.matches ? 0.625 : 1);
    ambient.intensity = settings.ambient;
    light.intensity = settings.light;
    light.shadow.radius = settings.shadowSoftness;
    lens.enabled = settings.postprocessing;
    camera.zoom = settings.zoom;
    camera.updateProjectionMatrix();
    updateGridTilt();
    draw();
  }

  const dayBrand = new THREE.Color("#ff0058");
  const nightBrand = new THREE.Color("#ae0040");
  const dayWhite = new THREE.Color("#ffffff");
  const nightWhite = new THREE.Color("#bfbfc5");

  function draw() {
    waveUniforms.panelColor.value.copy(dayBrand).lerp(nightBrand, darkness);
    waveUniforms.panelWhite.value.copy(dayWhite).lerp(nightWhite, darkness);
    syncThemeControls();
    ambient.intensity = settings.ambient * THREE.MathUtils.lerp(1, 0.45, darkness);
    light.intensity = settings.light * THREE.MathUtils.lerp(1, 0.25, darkness);
    spotlight.visible = darkness > 0.001;
    spotlight.intensity = 150 * darkness;
    waveUniforms.surfaceStrength.value = THREE.MathUtils.lerp(
      settings.opacity * (reducedMotion.matches ? 0.625 : 1), 1, darkness,
    );
    scene.background.copy(waveUniforms.panelColor.value)
      .lerp(whiteColor, waveUniforms.colorTarget.value).multiplyScalar(1 - darkness * 0.5);
    for (let i = 0; i < trail.length; i += 1) {
      const point = trail[i];
      const offset = i * 4;
      trailData[offset] = point.x;
      trailData[offset + 1] = point.z;
      trailData[offset + 2] = point.time;
      trailData[offset + 3] = point.strength;
    }
    if (trail.length || waveUniforms.waveCount.value) trailTexture.needsUpdate = true;
    waveUniforms.waveCount.value = trail.length;
    waveUniforms.waveTime.value = time;
    aboutFlight?.update();
    composer.render();
  }

  function resize() {
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const aspect = width / height;
    const halfHeight = Math.min(5.5, 13 / aspect);
    camera.aspect = aspect;
    camera.fov = THREE.MathUtils.radToDeg(2 * Math.atan(halfHeight / camera.position.length()));
    camera.updateProjectionMatrix();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(width, height, false);
    composer.setPixelRatio(renderer.getPixelRatio());
    composer.setSize(width, height);
    lens.uniforms.resolution.value.set(width * renderer.getPixelRatio(), height * renderer.getPixelRatio());
    applySettings();
  }

  function addPoint(x, z, strength) {
    if (trail.length === 128) trail.shift();
    trail.push({ x, z, time, strength });
  }

  window.addEventListener(
    "pointermove",
    (event) => {
      if (pressGesture && Math.hypot(event.clientX - pressGesture.x, event.clientY - pressGesture.y) > 6)
        pressGesture = undefined;
      if (reducedMotion.matches) return;
      if (event.target.closest?.(".wave-debug")) {
        resetPointer();
        return;
      }
      const rect = canvas.getBoundingClientRect();
      pointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        1 - ((event.clientY - rect.top) / rect.height) * 2,
      );
      if (event.pointerType !== "touch") {
        tiltTarget.set(THREE.MathUtils.clamp(pointer.x, -1, 1), THREE.MathUtils.clamp(pointer.y, -1, 1));
      }
      if (!projectPointer()) return;
      if (waveUniforms.cursorPresence.value < 0.001) waveUniforms.cursorPoint.value.copy(targetPoint);
      if (!hasPointer) {
        smoothPoint.copy(targetPoint);
        lastPoint.copy(targetPoint);
      }
      hasPointer = true;
    },
    { passive: true },
  );

  function resetPointer() {
    tiltTarget.set(0, 0);
    hasPointer = false;
  }

  document.documentElement.addEventListener("pointerleave", resetPointer);
  window.addEventListener("blur", resetPointer);
  window.addEventListener("pointercancel", resetPointer);
  window.addEventListener("pointerup", (event) => {
    if (event.pointerType === "touch") resetPointer();
  });

  const controls = ".wave-debug, .app__logo, .app__card, a, button, input, select, textarea";
  window.addEventListener("pointerdown", (event) => {
    pressGesture =
      !reducedMotion.matches && event.isPrimary && event.button === 0 && !event.target.closest?.(controls)
        ? { id: event.pointerId, x: event.clientX, y: event.clientY }
        : undefined;
  });
  window.addEventListener("pointerup", (event) => {
    const gesture = pressGesture;
    pressGesture = undefined;
    if (
      !gesture ||
      gesture.id !== event.pointerId ||
      reducedMotion.matches ||
      event.target.closest?.(controls) ||
      Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 6
    )
      return;
    pressBackground(event);
  });
  function pressBackground(event) {
    if (reducedMotion.matches) return;
    const id = pickElement(event);
    if (id < 0 || id >= cubes.count) return;
    cubes.getMatrixAt(id, matrix);
    addPoint(matrix.elements[12], matrix.elements[14], settings.clickStrength);
    presses.set(id, { time, from: cubes.geometry.getAttribute("clickPress").getX(id) });
  }
  window.addEventListener("pointercancel", () => {
    pressGesture = undefined;
  });
  window.addEventListener("blur", () => {
    pressGesture = undefined;
  });

  function updateGridTilt() {
    // Rotate the shared plane around its center, including all instance positions.
    grid.rotation.set(
      THREE.MathUtils.degToRad(tilt.y * settings.tiltY),
      0,
      THREE.MathUtils.degToRad(tilt.x * settings.tiltX),
    );
    grid.updateMatrixWorld(true);
  }

  function projectPointer() {
    worldPlane.copy(plane).applyMatrix4(grid.matrixWorld);
    raycaster.setFromCamera(pointer, camera);
    if (!raycaster.ray.intersectPlane(worldPlane, hit)) return false;
    // Wave samples are stored in grid-local coordinates, also after tilting.
    grid.worldToLocal(hit);
    targetPoint.set(hit.x, hit.z);
    return true;
  }

  function animate(now) {
    const delta = previousTime === undefined ? 0 : Math.min((now - previousTime) / 1000, 0.05);
    previousTime = now;
    time += delta;
    darkness = THREE.MathUtils.damp(darkness, Number(darkMode), 6, delta);
    waveUniforms.colorElapsed.value = time - colorStart;
    tilt.lerp(tiltTarget, 1 - Math.exp(-delta * settings.tiltResponse));
    updateGridTilt();
    if (hasPointer) {
      projectPointer();
      waveUniforms.cursorPoint.value.lerp(targetPoint, 1 - Math.exp(-delta * settings.cursorResponse));
      smoothPoint.lerp(targetPoint, 1 - Math.exp(-delta * settings.pointerResponse));
      const distance = smoothPoint.distanceTo(lastPoint);
      if (distance > settings.trailSpacing && time - lastSample > 1 / 24) {
        addPoint(smoothPoint.x, smoothPoint.y, Math.min(distance, 0.7));
        lastPoint.copy(smoothPoint);
        lastSample = time;
      }
    }
    waveUniforms.cursorPresence.value = THREE.MathUtils.lerp(
      waveUniforms.cursorPresence.value,
      hasPointer ? 1 : 0,
      1 - Math.exp(-delta * settings.cursorResponse),
    );
    while (trail.length && time - trail[0].time > settings.waveFade * 4) trail.shift();
    const clickPress = cubes.geometry.getAttribute("clickPress");
    for (const [id, press] of presses) {
      const age = time - press.time;
      const attack = Math.min(0.12, settings.clickDuration * 0.2);
      const depth =
        THREE.MathUtils.lerp(press.from, settings.clickDepth, THREE.MathUtils.smoothstep(age, 0, attack)) *
        (1 - THREE.MathUtils.smoothstep(age, attack, settings.clickDuration));
      clickPress.setX(id, depth);
      clickPress.needsUpdate = true;
      if (age >= settings.clickDuration) presses.delete(id);
    }
    draw();
  }

  function syncAnimation() {
    if (reducedMotion.matches) darkness = Number(darkMode);
    previousTime = undefined;
    if (document.hidden) resetPointer();
    waveUniforms.surfaceStrength.value = settings.opacity * (reducedMotion.matches ? 0.625 : 1);
    renderer.setAnimationLoop(document.hidden || reducedMotion.matches ? null : animate);
    if (reducedMotion.matches) {
      colorStart = time - 1000000;
      waveUniforms.colorElapsed.value = 1000000;
      trail.length = 0;
      hasPointer = false;
      waveUniforms.cursorPresence.value = 0;
      presses.clear();
      const clickPress = cubes.geometry.getAttribute("clickPress");
      clickPress.array.fill(0);
      clickPress.needsUpdate = true;
      tilt.set(0, 0);
      tiltTarget.set(0, 0);
      updateGridTilt();
      draw();
    }
  }

  // Keep each instance a solid color; only its blend is staggered by distance.
  function colorAt(from, delay) {
    const blend = THREE.MathUtils.smoothstep(waveUniforms.colorElapsed.value - delay, 0, waveUniforms.colorDuration.value);
    return THREE.MathUtils.lerp(from, waveUniforms.colorTarget.value, blend);
  }
  // Animate the original instances, in world space, towards camera-aligned bento faces.
  createGridFlight = (tiles, states) => {
    if (reducedMotion.matches || settings.shape !== 'box') return null;
    const candidates = [];
    const point = new THREE.Vector3();
    for (let id = 0; id < cubes.count; id++) {
      cubes.getMatrixAt(id, matrix);
      point.set(0, 1, 0).applyMatrix4(matrix).applyMatrix4(grid.matrixWorld).project(camera);
      if (Math.abs(point.x) < 0.88 && Math.abs(point.y) < 0.86 && point.z < 1) candidates.push(id);
    }
    if (candidates.length < tiles.length) return null;
    const mesh = cubes;
    const cells = tiles.map((tile, index) => {
      const [id] = candidates.splice(Math.floor(Math.random() * candidates.length), 1);
      const origin = new THREE.Matrix4();
      cubes.getMatrixAt(id, origin);
      const style = getComputedStyle(tile);
      return {id, origin, inverse: origin.clone().invert(), tile, state: states[index], rect: tile.getBoundingClientRect(),
        radius: parseFloat(style.borderTopLeftRadius), cornerPower: CSS.supports('corner-shape', 'squircle') ? 4 : 2,
        bend: (Math.random() - 0.5) * 0.9};
    });
    const inverseGrid = new THREE.Matrix4();
    const world = new THREE.Matrix4();
    const position = new THREE.Vector3(), scale = new THREE.Vector3();
    const rotation = new THREE.Quaternion();
    const targetRotation = camera.quaternion.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2));
    const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
    const target = new THREE.Vector3(), targetScale = new THREE.Vector3();
    const attributes = ['flightX', 'flightY', 'flightZ', 'flightOrigin'].map(name => mesh.geometry.getAttribute(name));
    const progress = mesh.geometry.getAttribute('flightProgress');
    const shape = mesh.geometry.getAttribute('flightShape');
    let dirty = false;
    const invalidate = () => { dirty = true; };
    window.addEventListener('resize', invalidate);
    document.querySelector('#about').addEventListener('scroll', invalidate, {passive:true});
    aboutFlight = {
      update() {
        if (mesh !== cubes) return;
        const bounds = canvas.getBoundingClientRect();
        const depth = 8;
        const visibleHeight = 2 * depth * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) / camera.zoom;
        const unit = visibleHeight / bounds.height;
        inverseGrid.copy(grid.matrixWorld).invert();
        for (const [index, cell] of cells.entries()) {
          if (dirty) {
            cell.rect = cell.tile.getBoundingClientRect();
            cell.radius = parseFloat(getComputedStyle(cell.tile).borderTopLeftRadius);
          }
          const rect = cell.rect;
          const p = cell.state.progress;
          lens.uniforms.bentoRects.value[index].set(
            (rect.left - bounds.left) / bounds.width, 1 - (rect.bottom - bounds.top) / bounds.height,
            (rect.right - bounds.left) / bounds.width, 1 - (rect.top - bounds.top) / bounds.height);
          lens.uniforms.bentoPresence.value[index] = THREE.MathUtils.smoothstep(p, 0.55, 0.8);
          cell.tile.style.opacity = String(THREE.MathUtils.clamp((p - 0.8) / 0.12, 0, 1));
          const phase = Math.min(p / 0.8, 1);
          // Ease out without a flat endpoint, so reverse playback starts moving promptly.
          const travel = 0.2 * phase + 0.8 * Math.sin(phase * Math.PI / 2);
          progress.setX(cell.id, p);
          world.multiplyMatrices(grid.matrixWorld, cell.origin).decompose(position, rotation, scale);
          target.set((rect.left + rect.width / 2 - bounds.left - bounds.width / 2) * unit,
            -(rect.top + rect.height / 2 - bounds.top - bounds.height / 2) * unit, -depth - 0.00001)
            .applyMatrix4(camera.matrixWorld);
          // Flatten the settled block: its back and side faces must not protrude past the rounded front.
          targetScale.set(rect.width * unit / settings.shapeSize, 0.00001, rect.height * unit / settings.shapeSize);
          const arc = Math.sin(travel * Math.PI);
          const lift = 0.7 + Math.min(position.distanceTo(target) * 0.06, 0.7);
          position.lerp(target, travel)
            .addScaledVector(forward, arc * 2)
            .addScaledVector(up, arc * lift)
            .addScaledVector(right, arc * cell.bend);
          rotation.slerp(targetRotation, travel);
          scale.lerp(targetScale, travel);
          const halfWidth = settings.shapeSize * scale.x / 2;
          const halfHeight = settings.shapeSize * scale.z / 2;
          const radius = Math.min(cell.radius * unit, halfWidth, halfHeight) * THREE.MathUtils.smoothstep(p, 0.3, 0.8);
          shape.setXYZW(cell.id, halfWidth, halfHeight, radius, cell.cornerPower);
          world.compose(position, rotation, scale).premultiply(inverseGrid).premultiply(cell.inverse);
          const e = world.elements;
          attributes.forEach((attribute, index) => attribute.setXYZ(cell.id, e[index * 4], e[index * 4 + 1], e[index * 4 + 2]));
        }
        dirty = false;
        progress.needsUpdate = true;
        shape.needsUpdate = true;
        attributes.forEach(attribute => { attribute.needsUpdate = true; });
      },
      release() {
        lens.uniforms.bentoPresence.value.fill(0);
        progress.array.fill(0);
        shape.array.fill(0);
        shape.needsUpdate = true;
        progress.needsUpdate = true;
        window.removeEventListener('resize', invalidate);
        document.querySelector('#about').removeEventListener('scroll', invalidate);
        aboutFlight = undefined;
        draw();
      },
    };
    draw();
    return aboutFlight;
  };
  const themeControls = [...document.querySelectorAll(".app__logo, .app__info, .debug-launcher")]
    .map(element => ({ element, from: 0, delay: 0, value: 0 }));
  const controlColor = new THREE.Color();
  const whiteColor = waveUniforms.panelWhite.value;
  let controlDarkness = -1;
  function syncThemeControls() {
    for (const control of themeControls) {
      const value = colorAt(control.from, control.delay);
      if (value === control.value && controlDarkness === darkness) continue;
      control.value = value;
      control.element.style.setProperty("--bgColor", controlColor.copy(waveUniforms.panelColor.value).lerp(whiteColor, value).getStyle());
      control.element.style.setProperty("--gdColor", controlColor.copy(whiteColor).lerp(waveUniforms.panelColor.value, value).getStyle());
    }
    controlDarkness = darkness;
    const white = waveUniforms.colorTarget.value;
    const background = controlColor.copy(waveUniforms.panelColor.value).lerp(whiteColor, white).getStyle();
    document.documentElement.style.setProperty("--bgColor", background);
    document.documentElement.style.setProperty("--gdColor", controlColor.copy(whiteColor).lerp(waveUniforms.panelColor.value, white).getStyle());
    document.querySelector('meta[name="theme-color"]').content = background;
  }
  function screenPoint(clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    worldPlane.copy(plane).applyMatrix4(grid.matrixWorld);
    raycaster.setFromCamera(new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      1 - ((clientY - rect.top) / rect.height) * 2,
    ), camera);
    if (!raycaster.ray.intersectPlane(worldPlane, hit)) return new THREE.Vector2();
    grid.worldToLocal(hit);
    return new THREE.Vector2(hit.x, hit.z);
  }
  document.documentElement.addEventListener("themechange", ({ detail }) => {
    const origin = screenPoint(detail.clientX, detail.clientY);
    const delayAt = (x, z) => Math.hypot(x - origin.x, z - origin.y) / Math.max(0.1, settings.waveSpeed);
    for (const mesh of [cubes, backing]) {
      const from = mesh.geometry.getAttribute("colorFrom");
      const delay = mesh.geometry.getAttribute("colorDelay");
      const positions = mesh.geometry.getAttribute("position");
      for (let i = 0; i < from.count; i++) {
        from.setX(i, colorAt(from.getX(i), delay.getX(i)));
        const x = mesh === cubes ? cubes.instanceMatrix.array[i * 16 + 12] : positions.getX(i);
        const z = mesh === cubes ? cubes.instanceMatrix.array[i * 16 + 14] : positions.getZ(i);
        delay.setX(i, delayAt(x, z));
      }
      from.needsUpdate = delay.needsUpdate = true;
    }
    for (const control of themeControls) {
      control.from = colorAt(control.from, control.delay);
      const rect = control.element.getBoundingClientRect();
      const point = screenPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
      control.delay = delayAt(point.x, point.y);
      // Override the new root theme until the wave reaches this control.
      control.value = -1;
    }
    waveUniforms.colorTarget.value = Number(detail.white);
    colorStart = reducedMotion.matches ? time - 1000000 : time;
    waveUniforms.colorElapsed.value = time - colorStart;
    scene.background.copy(waveUniforms.panelColor.value).lerp(whiteColor, Number(detail.white));
    if (!reducedMotion.matches) addPoint(origin.x, origin.y, settings.clickStrength);
    draw();
  });
  const lightSwitch = document.querySelector(".light-switch");
  if (lightSwitch) {
    lightSwitch.setAttribute("aria-checked", String(darkMode));
    lightSwitch.hidden = false;
    lightSwitch.addEventListener("click", () => {
      darkMode = !darkMode;
      document.cookie = `darkMode=${darkMode}; Path=/; Max-Age=31536000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
      lightSwitch.setAttribute("aria-checked", String(darkMode));
      if (reducedMotion.matches) darkness = Number(darkMode);
      draw();
    });
  }
  resize();
  syncAnimation();
  window.addEventListener("resize", resize);
  document.addEventListener("visibilitychange", syncAnimation);
  reducedMotion.addEventListener("change", syncAnimation);

}
